import Head from "next/head"
import Image from "next/image"
import { useEffect, useState } from "react"

import { Container } from "@components/ui"
import { NextSeo } from "next-seo"
import { defaultDescription, domain } from "@components/common/Head"

const ClassicShareIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default function Article({
  htmlTitle,
  title,
  subtitle,
  slug,
  author,
  authorLink,
  date,
  ogDescription,
  ogImage,
  coverImage,
  children
}) {
  const [canNativeShare, setCanNativeShare] = useState(false)
  const [hasCopiedLink, setHasCopiedLink] = useState(false)
  const shareUrl = `${domain}/blog/${slug}`
  const socialTitle = htmlTitle || title
  const socialDescription = ogDescription || subtitle || defaultDescription
  const socialImage = ogImage
    ? `${domain}/blog/${ogImage}`
    : `${domain}/og_image.png`
  const twitterImage = coverImage
    ? `${domain}/blog/${coverImage}`
    : ogImage
    ? socialImage
    : `${domain}/twitter_card.png`

  useEffect(() => {
    const script = document.createElement("script")
    script.setAttribute("src", "https://platform.twitter.com/widgets.js")
    script.setAttribute("async", "")
    script.setAttribute("charset", "utf-8")
    document.body.appendChild(script)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return

    const mediaQuery = window.matchMedia("(max-width: 640px)")
    const updateShareCapability = () => {
      const isMobileViewport = mediaQuery.matches
      const nativeShareAvailable =
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function"
      setCanNativeShare(isMobileViewport && nativeShareAvailable)
    }

    updateShareCapability()

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateShareCapability)
      return () =>
        mediaQuery.removeEventListener("change", updateShareCapability)
    } else {
      mediaQuery.addListener(updateShareCapability)
      return () => mediaQuery.removeListener(updateShareCapability)
    }
  }, [])

  useEffect(() => {
    if (!hasCopiedLink) return
    const timeout = setTimeout(() => setHasCopiedLink(false), 2000)
    return () => clearTimeout(timeout)
  }, [hasCopiedLink])

  const handleShare = async () => {
    if (typeof window === "undefined") return

    if (canNativeShare && typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title,
          text: socialDescription,
          url: shareUrl
        })
      } catch (_) {
        // no-op when user cancels share
      }
      return
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl)
      } else {
        const tempInput = document.createElement("textarea")
        tempInput.value = shareUrl
        document.body.appendChild(tempInput)
        tempInput.select()
        document.execCommand("copy")
        document.body.removeChild(tempInput)
      }
      setHasCopiedLink(true)
    } catch (error) {
      console.error("Unable to share blog post", error)
    }
  }

  const shareButtonLabel = hasCopiedLink ? "Link copied" : "Share"

  return (
    <>
      <NextSeo
        title={htmlTitle || title}
        description={socialDescription}
        titleTemplate="%s | Slice Blog"
        openGraph={{
          title: socialTitle,
          description: socialDescription,
          url: `${domain}/blog/${slug}`,
          images: [
            {
              url: socialImage,
              alt: `${slug} blog post cover image`
            }
          ]
        }}
      />
      <Head>
        <meta name="twitter:title" content={socialTitle} />
        <meta name="twitter:description" content={socialDescription} />
        <meta name="twitter:image" content={twitterImage} />
      </Head>

      <section className="">
        <Container size="max-w-screen-xl">
          <div className="flex flex-col items-center pt-16 pb-8 text-center sm:pt-24 sm:pb-12">
            {date && (
              <div className="flex flex-wrap items-center justify-center gap-6 text-[0.7rem] font-semibold uppercase tracking-[0.35em] text-gray-400">
                <span>{date}</span>
              </div>
            )}
            <h1 className="mt-8 text-4xl font-medium tracking-tight sm:text-5xl md:text-6xl">
              {title}
            </h1>
            {subtitle ? (
              <p className="max-w-3xl mx-auto mt-6 text-lg text-gray-300 sm:text-xl">
                {subtitle}
              </p>
            ) : null}
          </div>
        </Container>
      </section>

      <Container size="max-w-screen-xl">
        <section className="pb-12">
          <div className="w-full max-w-3xl mx-auto text-left px-2 sm:px-0">
            <div
              className={`flex w-full gap-4 text-sm text-gray-400 sm:mt-10 flex-row items-center justify-between`}
            >
              {author && (
                <div className="text-base text-gray-400">
                  by{" "}
                  {authorLink ? (
                    <a
                      href={authorLink}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold transition-colors duration-200 hover:text-gray-600"
                    >
                      {author}
                    </a>
                  ) : (
                    <span className="font-semibold">{author}</span>
                  )}
                </div>
              )}
              <div className="flex justify-start sm:justify-end">
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-200 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                  aria-live="polite"
                >
                  <ClassicShareIcon />
                  <span>{shareButtonLabel}</span>
                </button>
              </div>
            </div>
            {coverImage ? (
              <div
                className="relative max-h-[53.35vw] sm:max-h-[360px] h-screen mt-10 overflow-hidden rounded-xl box-content"
                style={{ aspectRatio: "16 / 9" }}
              >
                <Image
                  src={`${domain}/blog/${coverImage}`}
                  alt={`Cover image of ${slug} post`}
                  fill
                  className="object-cover"
                />
              </div>
            ) : null}

            <article className="prose prose-lg md:prose-base max-w-none font-light">
              {children}
            </article>
          </div>
        </section>
      </Container>
    </>
  )
}

// coverImage needs to be 16/9 aspect ratio
